import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { Document, Font, Image, Line, PDFViewer, Page, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { theme } from 'theme';
import { currencyOf, showOnlyDate } from 'utils/util';

Font.register({
    family: "Anton",
    src: "/assets/fonts/anton.ttf",
});

export type PreVendaState = {
    numero: string;
    cliente: string;
    vendedor: string,
    emissao: Date | string,
    observacao: string;
    valorTotalLiquido: number;
    valorTotalBruto: number;
    valorAcrescimo: number;
};

export type PreVendaPdfProps = PreVendaState & {
    onClose: () => void;
};

const Input = (props: { label: string; value: string; withoutBorder?: boolean; }) => {
    return <View style={{ width: '100%' }}>
        <Text style={s.inputLabel}>{props.label}</Text>
        <Text style={s.inputValue}>{props.value} </Text>
        {
            !props.withoutBorder && <Svg
                height="5"
                width={'100%'}
                style={s.inputSvgBorder}>
                <Line
                    x1={0}
                    x2={400}
                    y1="0"
                    y2="0"
                    strokeWidth={3}
                    stroke={theme.palette.text.primary} />
            </Svg>
        }
    </View >;
};

const PreVendaPdf = (props: PreVendaPdfProps) => {
    const getSubString = (value: string) => {
        const breakPoint = 34;
        return `${value.substring(0, breakPoint)}${value.length >= breakPoint ? "..." : ''}`;
    };

    return (
        <>
            <IconButton
                sx={{
                    zIndex: 100000,
                    position: 'absolute',
                    bottom: 20,
                    right: 30,
                    backgroundColor: theme.palette.primary.main,
                    ':hover': {
                        backgroundColor: theme.palette.primary.main,
                        filter: 'grayscale(.2) brightness(.8) contrast(1.5)'
                    }
                }}
                onClick={props.onClose}
            >
                <CloseIcon sx={{ color: theme.palette.background.default, fontSize: 45 }} />
            </IconButton>

            <PDFViewer
                style={s.pdfView}
                showToolbar={true}
            >
                <Document
                    title='Pré-venda'
                    author='André Franco'
                    language='pt-BR'
                >
                    <Page
                        size={'B7'}
                        orientation='landscape'
                        style={{ ...s.page, backgroundColor: theme.palette.primary.main }}
                    >
                        <View style={s.titleWrapper}>
                            <Text style={s.title}>Pedido #{props.numero}</Text>
                            <Text style={s.subtitle}>{props.emissao ? showOnlyDate(props.emissao.toString()) : showOnlyDate(new Date().toString())}</Text>
                        </View>

                        <View style={s.contentWrapper}>
                            <View style={s.imgWrapper}>
                                <Image style={s.img} source={'assets/shopCar.png'} />
                            </View>

                            <View style={s.inputsWrapper}>
                                <Input label="Cliente" value={getSubString(props.cliente)} />
                                <Input label="Vendedor" value={getSubString(props.vendedor)} />

                                <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '5px' }}>
                                    <Input label="Total" value={currencyOf(props.valorTotalBruto)} />
                                    <Input label="Desconto" value={currencyOf(props.valorTotalBruto + props.valorAcrescimo - props.valorTotalLiquido)} />
                                    <Input label="Acréscimo" value={currencyOf(props.valorAcrescimo)} />
                                    <Input label="Total líquido" value={currencyOf(props.valorTotalLiquido)} />
                                </View>
                            </View>
                        </View>

                        <View style={s.obsWrapper}>
                            <Input label="Observacão:" value={props.observacao} withoutBorder />
                        </View>
                    </Page>
                </Document>
            </PDFViewer >
        </>
    );
};

const s = StyleSheet.create({
    pdfView: {
        position: 'absolute',
        width: '100%',
        height: '100vh',
        border: 0,
        top: 0,
        left: 0,
        zIndex: 1000
    },
    page: {
        position: 'relative',
    },

    titleWrapper: {
        height: '25%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        padding: '5px'
    },

    contentWrapper: {
        height: '50%',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
    },

    obsWrapper: {
        height: '25%',
        width: '100%',
        display: 'flex',
        padding: '0 10px 10px 10px'
    },

    imgWrapper: {
        height: '100%',
        width: '25%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    inputsWrapper: {
        height: '100%',
        width: '75%',
        paddingRight: '10px',
        paddingBottom: '7px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    },

    title: {
        fontSize: '23px',
        color: theme.palette.primary.contrastText,
        fontFamily: "Anton",
        fontWeight: 'extrabold',
        textAlign: 'center',
        borderTop: "1px",
    },

    subtitle: {
        fontSize: '12px',
        color: theme.palette.primary.contrastText,
        fontFamily: "Anton",
        fontWeight: 'extrabold',
        textAlign: 'center',
        borderBottom: "1px"
    },

    img: {
        objectFit: 'cover',
        width: '130%',
    },

    inputLabel: {
        fontSize: '10px',
        width: '100%',
        color: theme.palette.common.black,
        fontWeight: 'extralight',
        marginBottom: '3px',
    },

    inputValue: {
        fontSize: '12px',
        minHeight: '35px',
        width: '100%',
        color: theme.palette.common.black,
        fontWeight: 'normal',
        padding: '0px',
    },

    inputSvgBorder: {
        marginTop: '-18px',
    }
});

export default PreVendaPdf;
